import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mic, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';

const AgentTest: React.FC = () => {
  const [transcriptText, setTranscriptText] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  const startInterview = async () => {
    try {
      setIsConnecting(true);
      
      // Request microphone permission
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micError) {
        throw new Error('Microphone permission denied. Please allow microphone access to continue.');
      }

      // Simulating connection
      setTimeout(() => {
        setIsConnecting(false);
        toast.success('Connected to interviewer');
        setCurrentQuestion('Tell me about your experience with React.');
        setTranscriptText('AI: Hello! I\'ll be conducting your technical assessment today. Tell me about your experience with React.\n');
      }, 1500);

    } catch (error) {
      console.error('Failed to start interview:', error);
      setIsConnecting(false);
      toast.error(error instanceof Error ? error.message : 'Failed to start interview');
    }
  };

  const toggleMute = async () => {
    try {
      setIsMuted(!isMuted);
      toast.success(isMuted ? 'Audio unmuted' : 'Audio muted');
    } catch (error) {
      console.error('Error toggling mute:', error);
      toast.error('Failed to toggle mute');
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      <h1 className="text-2xl font-bold mb-6">Agent Test</h1>
      
      <Card className="mb-8">
        <CardContent className="pt-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-2">Current Question:</h2>
            <p>{currentQuestion || 'No question yet...'}</p>
          </div>

          <div className="flex justify-center items-center gap-4 min-h-[100px]">
            {isConnecting ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p>Connecting to interviewer...</p>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Button 
                  onClick={startInterview}
                  disabled={isConnecting}
                  className="flex items-center gap-2"
                >
                  <Mic className="h-5 w-5" />
                  Start Interview
                </Button>

                <Button
                  variant="outline"
                  onClick={toggleMute}
                  className="flex items-center gap-2"
                >
                  {isMuted ? (
                    <VolumeX className="h-5 w-5" />
                  ) : (
                    <Volume2 className="h-5 w-5" />
                  )}
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
              </div>
            )}
          </div>

          {isSpeaking && (
            <div className="flex items-center justify-center gap-2 text-muted-foreground mt-4">
              <div className="animate-pulse">AI is speaking...</div>
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-lg font-semibold mb-4">Live Transcript</h2>
        <div className="bg-muted p-4 rounded-lg min-h-[200px] whitespace-pre-wrap">
          {transcriptText || 'Transcript will appear here...'}
        </div>
      </div>
    </div>
  );
};

export default AgentTest; 